import { useState, useEffect, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import BankAccountService from "../../../../services/bank_account_service";
import { COLLECTIONS } from "../../../../services/collections";

/**
 * useBankAccounts
 * Hook de lógica para o módulo de Contas Bancárias.
 */
export const useBankAccounts = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "description",
        orderDirection: "asc"
    }), []);

    // Paginação das Contas Bancárias
    const { data: accounts, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.BANK_ACCOUNTS}`,
        idTenant,
        paginationOptions
    );

    // Marca fim do carregamento inicial na primeira vez que loading passa a false
    useEffect(() => {
        if (!loading && isInitialLoad) {
            setIsInitialLoad(false);
        }
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Salvar uma Conta Bancária
     */
    const saveAccount = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await BankAccountService.save(formData, idTenant, user, accounts);
            toast.success(formData.id ? "Conta atualizada!" : "Nova conta bancária cadastrada!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar conta bancária:", err);
            toast.error("Erro ao salvar conta bancária.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, accounts, refresh]);

    /**
     * Excluir uma Conta Bancária
     */
    const deleteAccount = useCallback(async (accountId) => {
        if (!idTenant) return;

        const accountToDelete = accounts.find(a => a.id === accountId);

        const confirmed = await ConfirmDialog({
            title: "Excluir Conta Bancária?",
            text: `Tem certeza que deseja excluir "${accountToDelete?.description || 'esta conta'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await BankAccountService.delete(accountId, idTenant, user, accountToDelete?.description);
            toast.success("Conta bancária removida!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir conta bancária:", err);
            toast.error("Não foi possível excluir a conta.");
            return false;
        }
    }, [idTenant, user, accounts, refresh]);

    return {
        accounts,
        loading,
        isInitialLoad,
        error,
        isSaving,
        actions: {
            saveAccount,
            deleteAccount,
            refresh
        }
    };
};
